import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './generated/schema';
import type { TokenStore } from '../auth/token-store';

export interface ApiClientOptions {
  baseUrl: string;
  tokenStore: TokenStore;
  /** Optional locale sent via Accept-Language. */
  locale?: string;
  /** Hook called when the API returns 401 — e.g. clear store and redirect to login. */
  onUnauthorized?: () => void;
}

export type ApiClient = ReturnType<typeof createApiClient>;

export const createApiClient = (options: ApiClientOptions) => {
  const client = createClient<paths>({
    baseUrl: options.baseUrl,
    headers: {
      Accept: 'application/json',
    },
  });

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const token = await options.tokenStore.get();
      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }
      if (options.locale) {
        request.headers.set('Accept-Language', options.locale);
      }
      return request;
    },
    async onResponse({ response }) {
      if (response.status === 401) {
        await options.tokenStore.clear();
        options.onUnauthorized?.();
      }
      return response;
    },
  };

  client.use(authMiddleware);
  return client;
};
